import { useEffect, useRef, useState } from "react";
import { UserIcon, XMarkIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../common/Button";
import { CallParticipant } from "../../db/schema";
import { SpeakerSuggestion } from "./api";

const label = "block text-[11px] font-semibold uppercase tracking-wider text-base-content/60 mb-1.5";
const field = "w-full rounded-lg border border-gray-300 dark:border-base-300 bg-base-100 px-3 py-2 text-sm text-base-content placeholder:text-base-content/40 outline-none focus:border-blue-500 transition-colors";

const ROLES: { value: NonNullable<CallParticipant["role"]>; label: string; hint: string }[] = [
  { value: "inmate", label: "Inmate", hint: "The incarcerated caller" },
  { value: "outside", label: "Outside party", hint: "The person who was called" },
  { value: "third_party", label: "Third party", hint: "Someone patched in or handed the phone" },
  { value: "unknown", label: "Unknown", hint: "Not sure yet" },
];

interface Props {
  open: boolean;
  label: string; // ch_0 / spk_1
  color?: string; // tailwind bg class for the speaker dot
  participant?: CallParticipant;
  // Same-number guess from other calls, when this speaker is unnamed.
  suggestion?: SpeakerSuggestion;
  // Other calls to this number whose outside speaker is still unnamed.
  propagateCount?: number;
  onClose: () => void;
  onSave: (values: { name: string; role: string; propagate: boolean }) => Promise<void>;
  onDismissSuggestion?: (name: string) => Promise<void>;
}

// Name + role for one transcript speaker. Replaces the old window.prompt pair.
const SpeakerModal: React.FC<Props> = ({ open, label: speakerLabel, color, participant, suggestion, propagateCount = 0, onClose, onSave, onDismissSuggestion }) => {
  const [name, setName] = useState("");
  const [role, setRole] = useState<string>("unknown");
  const [propagate, setPropagate] = useState(true);
  const [saving, setSaving] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setName(participant?.name || "");
    setRole(participant?.role || (suggestion ? "outside" : "unknown"));
    setPropagate(true);
    setTimeout(() => nameRef.current?.focus(), 0);
  }, [open, participant, suggestion]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && !saving && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, saving, onClose]);

  if (!open) return null;

  const save = async () => {
    setSaving(true);
    try {
      await onSave({ name: name.trim(), role, propagate: propagate && propagateCount > 0 && ROLES.some((r) => r.value === role && r.value !== "inmate" && r.value !== "unknown") });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <dialog open className="modal modal-open">
      <form
        method="dialog"
        className="modal-box max-w-md p-0 overflow-hidden rounded-2xl"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <div className="flex items-start gap-3 px-6 pt-6 pb-4 border-b border-base-300">
          <div className="size-10 shrink-0 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center">
            <UserIcon className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-base-content leading-tight">Identify speaker</h3>
            <p className="text-sm text-base-content/60 mt-0.5">
              {color && <span className={cn("inline-block size-2.5 rounded-full mr-1.5 align-middle", color)} />}
              Applies to every line on channel <span className="font-mono text-xs">{speakerLabel}</span>
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={saving} className="p-1 rounded-md text-base-content/50 hover:bg-base-200 hover:text-base-content" title="Close">
            <XMarkIcon className="size-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {suggestion && !name && (
            <div className="rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50/60 dark:bg-blue-900/20 p-3 flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-base-content">
                  Probably <span className="font-semibold">{suggestion.name}</span>
                  <span className="text-base-content/60"> — {suggestion.source === "detective" ? "you identified" : "the AI heard"} this name on {suggestion.support} other call{suggestion.support === 1 ? "" : "s"} to this number.</span>
                </p>
                {suggestion.conflicts.length > 0 && <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-0.5">Also heard: {suggestion.conflicts.join(", ")}</p>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button type="button" variant="primary" size="xs" onClick={() => setName(suggestion.name)}>
                  Use this name
                </Button>
                {onDismissSuggestion && (
                  <Button type="button" variant="outline" size="xs" onClick={() => onDismissSuggestion(suggestion.name).then(onClose)} disabled={saving}>
                    Not them
                  </Button>
                )}
              </div>
            </div>
          )}
          <div>
            <label className={label} htmlFor="spk-name">Name</label>
            <input id="spk-name" ref={nameRef} className={field} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Kelly Tibbs" maxLength={100} />
          </div>
          <div>
            <span className={label}>Role</span>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {ROLES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRole(r.value)}
                  className={cn(
                    "text-left rounded-lg border px-3 py-2 transition-colors",
                    role === r.value ? "border-blue-500 bg-blue-50 dark:bg-blue-900/40" : "border-base-300 hover:border-base-content/30 hover:bg-base-200"
                  )}
                >
                  <span className={cn("block text-sm font-medium", role === r.value ? "text-blue-600 dark:text-blue-300" : "text-base-content")}>{r.label}</span>
                  <span className="block text-[11px] text-base-content/50">{r.hint}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {propagateCount > 0 && role !== "inmate" && role !== "unknown" && name.trim() && (
          <label className="mx-6 mb-4 flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="checkbox checkbox-sm mt-0.5" checked={propagate} onChange={(e) => setPropagate(e.target.checked)} />
            <span>
              Also name the outside speaker on <span className="font-semibold">{propagateCount}</span> other call{propagateCount === 1 ? "" : "s"} to this number that {propagateCount === 1 ? "hasn't" : "haven't"} been identified yet
            </span>
          </label>
        )}
        <div className="flex items-center justify-between gap-3 px-6 py-4 bg-base-200/60 border-t border-base-300">
          <span className="text-[11px] text-base-content/40 hidden sm:inline">Enter to save · Esc to cancel</span>
          <div className="flex items-center gap-2 ml-auto">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="sm" loading={saving} disabled={saving}>
              Save
            </Button>
          </div>
        </div>
      </form>
      <div className="modal-backdrop bg-black/40" onClick={() => !saving && onClose()} />
    </dialog>
  );
};

export default SpeakerModal;
